import { useEffect, useState, type FormEvent } from "react";
import { gaPricer, track } from "../lib/analytics";
import { AddressFields, type AddressValue } from "../components/AddressFields";
import { savedPostalCode } from "../components/ShippingEstimator";
import { DISPATCH_HOURS, freeShippingEnabled, freeShippingMin, freeShippingZones, isFastZone, isShippingZone, pickupArea, pickupEnabled, quoteShipping, SHIPPING_ZONES, shippingRates, suggestZone, ZONE_LABEL } from "../../shared/shipping";
import { Link } from "react-router";
import { validateOrderInput } from "../../shared/order";
import { PROVIDER_CURRENCY, computeCharge, type PaymentProvider } from "../../shared/payments";
import { couponAmount, type CouponQuote } from "../../shared/coupons";
import type { OrderInput } from "../../shared/types";
import { variantLabel } from "../../shared/variants";
import { useAuth } from "../auth/AuthProvider";
import { useCart } from "../cart/CartProvider";
import { ErrorSummary } from "../components/form";
import { Loading } from "../components/StatusMessage";
import { DEFAULT_MP_FEE_PCT, formatArs } from "../../shared/money";
import { useI18n } from "../i18n/I18nProvider";
import { useMoney } from "../lib/CurrencyProvider";
import type { MessageKey } from "../i18n/messages";
import { ApiError, api, formatCharge } from "../lib/api";
import { useCatalog } from "../lib/CatalogProvider";
import { useAsync } from "../lib/useAsync";

type Field = "name" | "email" | "phone" | "zone" | "notes";
const FIELDS: { name: "name" | "email" | "phone"; type: string; autoComplete: string; inputMode?: "tel" | "email" }[] = [
  { name: "name", type: "text", autoComplete: "name" },
  { name: "email", type: "email", autoComplete: "email", inputMode: "email" },
  { name: "phone", type: "tel", autoComplete: "tel", inputMode: "tel" },
];
const EMPTY_ADDRESS: AddressValue = { province: "", city: "", addressLine: "", addressLine2: "", postalCode: "" };
const FIELD_IDS: Record<string, string> = { name: "co-name", email: "co-email", phone: "co-phone", city: "co-city", address: "co-line1", zone: "co-zone", delivery: "co-zone", paymentProvider: "co-pay" };

/** Three steps: who you are, how it gets to you, how you pay. Each field's errors belong to one step. */
type Step = 1 | 2 | 3;
const STEP_OF: Record<string, Step> = { name: 1, email: 1, phone: 1, city: 1, delivery: 2, address: 2, zone: 2, notes: 2 };
const stepOf = (field: string): Step => STEP_OF[field] ?? 3;

/** Browser navigation to the provider; isolated so tests can observe it. */
export const redirectTo = { go: (url: string) => window.location.assign(url) };

export function CheckoutPage() {
  const { t, pick, locale } = useI18n();
  const money = useMoney();
  const { formatLines } = money;
  const auth = useAuth();
  const { state, clear } = useCart();
  const { settings } = useCatalog();
  const payments = useAsync((signal) => api.paymentConfig(signal), []);
  const [form, setForm] = useState<Record<Field, string>>({ name: "", email: "", phone: "", zone: "", notes: "" });
  const [step, setStep] = useState<Step>(1);
  // The postal code typed on a product page ("Calculá el envío") comes along.
  const [address, setAddress] = useState<AddressValue>(() => ({ ...EMPTY_ADDRESS, postalCode: savedPostalCode() }));
  const [chosenDelivery, setDelivery] = useState<OrderInput["delivery"]>("shipping");
  const [provider, setProvider] = useState<PaymentProvider | null>(null);
  const [couponText, setCouponText] = useState("");
  const [coupon, setCoupon] = useState<CouponQuote | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attempt, setAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  // Analytics: checkout started, once per visit to the page.
  useEffect(() => {
    if (!state.lines.length) return;
    const ga = gaPricer(money);
    track("begin_checkout", {
      currency: ga.currency,
      value: state.lines.reduce((n, l) => n + ga.price(l.price, l.priceArs) * l.quantity, 0),
      items: state.lines.map((l) => ({ item_id: String(l.variantId), item_name: l.name.es, price: ga.price(l.price, l.priceArs), quantity: l.quantity })),
    });
    // Only on arrival: later cart edits are not a new checkout.
  }, []);

  // Prefill from the session and the saved profile (Mi cuenta); never overwrite what was typed.
  useEffect(() => {
    if (!auth.user) return;
    setForm((f) => (f.name ? f : { ...f, name: auth.user!.name }));
    const ctrl = new AbortController();
    api
      .profile(ctrl.signal)
      .then((p) => {
        setForm((f) => ({ ...f, phone: f.phone || (p.phone ?? "") }));
        setAddress((a) =>
          a.province || a.city
            ? a
            : { province: p.province ?? "", city: p.city ?? "", addressLine: p.addressLine ?? "", addressLine2: p.addressLine2 ?? "", postalCode: p.postalCode ?? "" },
        );
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, [auth.user]);

  // Preselect the only available provider.
  useEffect(() => {
    const list = payments.data?.providers ?? [];
    if (list.length === 1) setProvider(list[0]);
  }, [payments.data]);

  if (redirecting) return <Loading label={t("checkout.redirecting")} />;
  if (auth.loading) return <Loading />;

  if (state.lines.length === 0) {
    return (
      <div className="container section status">
        <h1>{t("checkout.title")}</h1>
        <p>{t("cart.empty")}</p>
        <Link to="/mercado" className="btn btn-primary">
          {t("cart.continue")}
        </Link>
      </div>
    );
  }

  const user = auth.user;
  const email = user?.email ?? form.email.trim();
  const providers = payments.data?.providers ?? [];
  const rate = payments.data?.usdArsRate ?? null;
  const discountPct = payments.data?.manualDiscountPct ?? 0;
  const chargeLines = state.lines.map((l) => ({ unitPrice: l.price, unitPriceArs: l.priceArs ?? null, quantity: l.quantity }));
  const freeMin = freeShippingMin(settings.free_shipping_min_ars ? pick(settings.free_shipping_min_ars) : null);
  const freeOn = freeShippingEnabled(settings.free_shipping_enabled ? pick(settings.free_shipping_enabled) : null);
  const freeZones = freeShippingZones(settings.free_shipping_zones ? pick(settings.free_shipping_zones) : null);
  const rates = shippingRates(settings.shipping_rates_ars ? pick(settings.shipping_rates_ars) : null);
  // Pickup: free, at a point whose address we send on WhatsApp after the purchase (Admin › Pedidos).
  const pickupOn = pickupEnabled(settings.pickup_enabled ? pick(settings.pickup_enabled) : null);
  const pickupWhere = pickupArea(settings.pickup_area ? pick(settings.pickup_area) : null);
  const delivery: OrderInput["delivery"] = pickupOn ? chosenDelivery : "shipping";
  const productsTotal = chargeLines.reduce((n, l) => n + l.unitPrice * l.quantity, 0);
  const zone = isShippingZone(form.zone) ? form.zone : null;
  // Same rules as the server (shared/shipping.ts), which recomputes it when the order is placed.
  const quote = quoteShipping(delivery, zone, productsTotal, rate, { rates, freeEnabled: freeOn, freeMinArs: freeMin, freeZones });
  const freeShipping = quote.free;
  /** What the shopper should expect for the chosen zone (and free shipping, while it is on). */
  const shippingNote = !zone
    ? null
    : !isFastZone(zone)
      ? rates.other
        ? t("checkout.zoneOtherRate", { price: formatArs(rates.other, locale) })
        : t("checkout.zoneOther")
      : freeShipping
        ? t("checkout.freeShipping", { hours: DISPATCH_HOURS })
        : `${t("checkout.zoneFast", { price: formatArs(rates[zone], locale), hours: DISPATCH_HOURS })}${freeOn && freeZones.includes(zone) ? ` ${t("checkout.freeFrom", { min: formatArs(freeMin, locale) })}` : ""}`;
  const mpFeePct = payments.data?.mpFeePct ?? DEFAULT_MP_FEE_PCT;
  const chargeCoupon = coupon ? { kind: coupon.kind, value: coupon.value, eligible: state.lines.map((l) => !coupon.excludedVariantIds.includes(l.variantId)) } : null;
  const chargeFor = (p: PaymentProvider) => {
    const shippingArs = quote.ars ?? 0;
    if ((PROVIDER_CURRENCY[p] === "ARS" || shippingArs > 0 || coupon?.kind === "fixed") && !rate) return null;
    return computeCharge(chargeLines, p, rate, shippingArs, { mpFeePct, cryptoDiscountPct: discountPct, coupon: chargeCoupon });
  };
  /** Exactly what the provider will charge: USD as listed, or pesos (Mercado Pago or transfer price), coupon included. */
  const chargePreview = (p: PaymentProvider) => {
    const charge = chargeFor(p);
    if (!charge) return "";
    // Crypto is paid in USDT (1 USDT = US$1).
    return p === "crypto" ? `${(charge.amountMinor / 100).toFixed(2)} USDT` : formatCharge(charge.amountMinor, charge.currency, locale);
  };

  const payload = () => ({
    customer: {
      ...form,
      email,
      // The order keeps one line each: "City, Province" and "Street, floor/apt, CP".
      city: address.province && address.city ? `${address.city}, ${address.province}` : "",
      address: [address.addressLine, address.addressLine2, address.postalCode && `CP ${address.postalCode}`].filter(Boolean).join(", "),
    },
    delivery,
    locale,
    paymentProvider: provider,
    items: state.lines.map((l) => ({ variantId: l.variantId, quantity: l.quantity })),
    couponCode: coupon?.code,
  });

  /** Errors of the steps up to `upTo`, from the same validation the server runs. */
  const errorsUpTo = (upTo: Step) => {
    const validation = validateOrderInput(payload());
    return Object.fromEntries(Object.entries(validation.errors ?? {}).filter(([field]) => stepOf(field) <= upTo));
  };
  /** Shows the first step that has a problem, with its errors. */
  const showErrors = (found: Record<string, string>) => {
    const first = Math.min(...Object.keys(found).map(stepOf)) as Step;
    if (!Number.isFinite(first)) return setErrors(found);
    // Only that step's problems: the summary links to fields on screen.
    setErrors(Object.fromEntries(Object.entries(found).filter(([field]) => stepOf(field) === first)));
    setStep(first);
  };
  const next = () => {
    setAttempt((n) => n + 1);
    const found = errorsUpTo(step);
    if (Object.keys(found).length) return showErrors(found);
    setErrors({});
    setStep((s) => (s < 3 ? ((s + 1) as Step) : s));
    window.scrollTo?.({ top: 0, behavior: "smooth" });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (step < 3) return next();
    setAttempt((n) => n + 1);
    const validation = validateOrderInput(payload());
    if (!validation.ok || !validation.value) {
      showErrors(validation.errors);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const receipt = await api.createOrder(validation.value);
      clear();
      setRedirecting(true);
      redirectTo.go(receipt.redirectUrl);
    } catch (err) {
      if (err instanceof ApiError && err.code === "coupon_invalid") {
        // The coupon stopped working meanwhile (used up, expired…): take it off and say why.
        setCoupon(null);
        setCouponError(((err.details as Record<string, string>)?.coupon ?? "checkout.coupon.not_found") as string);
      } else if (err instanceof ApiError && err.status === 422 && err.details && typeof err.details === "object") {
        const details = err.details as Record<string, string>;
        // The server knows the city's zone: switch to it so the right shipping cost shows.
        if (isShippingZone(details.expected)) {
          const expected = details.expected;
          setForm((f) => ({ ...f, zone: expected }));
        }
        const { expected: _expected, ...fieldErrors } = details;
        showErrors(fieldErrors);
      } else if (err instanceof ApiError && err.code === "too_many_open_orders") {
        setErrors({ form: "checkout.errors.openOrders" });
      } else if (err instanceof ApiError && err.status === 409) {
        setErrors({ form: "checkout.errors.stock" });
      } else if (err instanceof ApiError && (err.status === 502 || err.status === 503)) {
        setErrors({ form: "checkout.errors.paymentProvider" });
      } else {
        setErrors({ form: "checkout.errors.network" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const errorText = (field: string) => (errors[field] ? t(errors[field] as MessageKey) : null);

  return (
    <div className="container section">
      <h1>{t("checkout.title")}</h1>
      <div className="checkout">
        <form className="checkout-form" onSubmit={submit} noValidate>
          <ol className="co-steps" aria-label={t("checkout.steps")}>
            {([1, 2, 3] as Step[]).map((n) => (
              <li key={n} className={n === step ? "is-current" : n < step ? "is-done" : undefined} aria-current={n === step ? "step" : undefined}>
                {n < step ? (
                  <button type="button" onClick={() => setStep(n)}>
                    <span className="co-step-n" aria-hidden="true">✓</span> {t(`checkout.step${n}`)}
                  </button>
                ) : (
                  <span>
                    <span className="co-step-n" aria-hidden="true">{n}</span> {t(`checkout.step${n}`)}
                  </span>
                )}
              </li>
            ))}
          </ol>
          <ErrorSummary errors={errors} fieldIds={FIELD_IDS} focusKey={attempt} />
          {step === 1 && (
            <>
              <fieldset>
                <legend>{t("checkout.contact")}</legend>
                {user ? (
                  <p className="muted">{t("checkout.accountEmail", { email: user.email })}</p>
                ) : (
                  <p className="muted small">
                    {t("checkout.guestHint")} <Link to="/ingresar?next=%2Fcheckout">{t("checkout.guestLogin")}</Link>
                  </p>
                )}
                {FIELDS.filter((f) => f.name !== "email" || !user).map((f) => (
                  <div key={f.name} className="field">
                    <label htmlFor={FIELD_IDS[f.name]}>{t(`checkout.${f.name}`)}</label>
                    <input
                      id={FIELD_IDS[f.name]}
                      name={f.name}
                      type={f.type}
                      inputMode={f.inputMode}
                      autoComplete={f.autoComplete}
                      required
                      value={form[f.name]}
                      aria-invalid={Boolean(errors[f.name])}
                      aria-describedby={errors[f.name] ? `${FIELD_IDS[f.name]}-err` : undefined}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    />
                    {f.name === "phone" && <span className="field-hint">{t("checkout.phoneHint")}</span>}
                    {f.name === "email" && <span className="field-hint">{t("checkout.emailHint")}</span>}
                    {errors[f.name] && (
                      <span id={`${FIELD_IDS[f.name]}-err`} className="field-error">
                        {errorText(f.name)}
                      </span>
                    )}
                  </div>
                ))}
                <AddressFields
                  idPrefix="co"
                  value={address}
                  onChange={(patch) => setAddress((a) => ({ ...a, ...patch }))}
                  show="place"
                  errors={{ city: errors.city ? t("checkout.errors.place") : null }}
                  onPlace={(provinceId, department) => {
                    const zone = suggestZone(provinceId, department);
                    if (zone) setForm((f) => ({ ...f, zone }));
                  }}
                />
              </fieldset>
            </>
          )}
          {step === 2 && (
            <>
              <fieldset>
                <legend>{pickupOn ? t("checkout.delivery") : t("checkout.shipping")}</legend>
                {pickupOn && (
                  <div className="radio-cards">
                    <label className={delivery === "shipping" ? "radio-card is-selected" : "radio-card"}>
                      <input type="radio" name="delivery" value="shipping" checked={delivery === "shipping"} onChange={() => setDelivery("shipping")} />
                      <span>
                        <strong>{t("checkout.shipping")}</strong>
                      </span>
                    </label>
                    <label className={delivery === "pickup" ? "radio-card is-selected" : "radio-card"}>
                      <input type="radio" name="delivery" value="pickup" checked={delivery === "pickup"} onChange={() => setDelivery("pickup")} />
                      <span>
                        <strong>{t("checkout.pickup")}</strong>
                        <span className="muted small">{t("checkout.pickupHint", { area: pickupWhere })}</span>
                      </span>
                    </label>
                  </div>
                )}
                {delivery === "pickup" && <p className="notice">{t("checkout.pickupNote")}</p>}
                {delivery === "shipping" && (
                  <>
                    <AddressFields
                      idPrefix="co"
                      value={address}
                      onChange={(patch) => setAddress((a) => ({ ...a, ...patch }))}
                      show="street"
                      errors={{ addressLine: errors.address ? errorText("address") : null }}
                    />
                    <div className="field">
                      <label htmlFor="co-zone">{t("checkout.zone")}</label>
                      <select
                        id="co-zone"
                        required
                        value={form.zone}
                        aria-invalid={Boolean(errors.zone)}
                        aria-describedby={errors.zone ? "co-zone-err" : undefined}
                        onChange={(e) => setForm({ ...form, zone: e.target.value })}
                      >
                        <option value="">{t("checkout.zoneChoose")}</option>
                        {SHIPPING_ZONES.map((z) => (
                          <option key={z} value={z}>
                            {ZONE_LABEL[z][locale]} — {isFastZone(z) ? formatArs(rates[z], locale) : rates.other ? formatArs(rates.other, locale) : t("checkout.zoneArrange")}
                          </option>
                        ))}
                      </select>
                      {errors.zone && (
                        <span id="co-zone-err" className="field-error">
                          {errorText("zone")}
                        </span>
                      )}
                    </div>
                    {shippingNote && <p className={freeShipping && zone && isFastZone(zone) ? "notice-ok" : "muted small"}>{shippingNote}</p>}
                  </>
                )}
                <p className="muted small">{t("checkout.packaging")}</p>
                <div className="field">
                  <label htmlFor="co-notes">{t("checkout.notes")}</label>
                  <textarea id="co-notes" rows={3} maxLength={500} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </fieldset>
            </>
          )}
          {step === 3 && (
            <>
              <fieldset>
                <legend>{t("checkout.payment")}</legend>
                {payments.loading ? (
                  <Loading />
                ) : providers.length === 0 ? (
                  <p className="field-error">{t("checkout.pay.none")}</p>
                ) : (
                  <div className="pay-options" id="co-pay">
                    {providers.map((p) => (
                      <label key={p} className={provider === p ? "pay-option is-selected" : "pay-option"}>
                        <input type="radio" name="payment" value={p} checked={provider === p} onChange={() => setProvider(p)} />
                        <span className="pay-option-body">
                          <strong>{t(`checkout.pay.${p}`)}</strong>
                          <small>{t(`checkout.pay.${p}Hint`, { amount: chargePreview(p), discount: discountPct })}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
                {errors.paymentProvider && <span className="field-error">{errorText("paymentProvider")}</span>}
                {rate && provider && PROVIDER_CURRENCY[provider] === "ARS" && (
                  <p className="muted small">{t("checkout.pay.arsNote", { rate: formatArs(Math.round(rate), locale) })}</p>
                )}
              </fieldset>
              {settings.shipping_note && <p className="muted">{pick(settings.shipping_note)}</p>}
            </>
          )}
          <div className="co-nav">
            {step > 1 && (
              <button type="button" className="btn btn-link" onClick={() => (setErrors({}), setStep((step - 1) as Step))}>
                ← {t("checkout.back")}
              </button>
            )}
            {step < 3 ? (
              <button type="submit" className="btn btn-primary btn-lg">
                {t("checkout.continue")}
              </button>
            ) : (
              <button type="submit" className="btn btn-primary btn-lg" disabled={submitting || providers.length === 0}>
                {submitting ? t("checkout.submitting") : t("checkout.submitPay")}
              </button>
            )}
          </div>
        </form>

        <aside className="checkout-summary" aria-labelledby="summary-title">
          <h2 id="summary-title">{t("checkout.summary")}</h2>
          <ul>
            {state.lines.map((l) => (
              <li key={l.variantId}>
                <span>
                  {l.quantity} × {pick(l.name)}
                  <small className="muted"> · {variantLabel(l.variant, locale)}</small>
                  {l.presale && <span className="tag tag-presale tag-inline">{t("product.presale")}</span>}
                </span>
                <span>{formatLines([{ unitPrice: l.price, unitPriceArs: l.priceArs, quantity: l.quantity }], { card: provider === "mercadopago" })}</span>
              </li>
            ))}
          </ul>
          {state.lines.some((l) => l.presale) && <p className="presale-note">{t("cart.presaleNote")}</p>}
          <div className="subtotal">
            <span>{t(delivery === "shipping" ? "checkout.subtotal" : "checkout.total")}</span>
            <strong>{formatLines(chargeLines, { card: provider === "mercadopago" })}</strong>
          </div>
          {delivery === "shipping" && (
            <div className="subtotal shipping-row">
              <span>{t("checkout.shippingRow")}</span>
              <strong>
                {!zone ? t("checkout.shippingChooseZone") : quote.ars === null ? t("checkout.shippingArrange") : quote.ars === 0 ? t("checkout.shippingFree") : formatArs(quote.ars, locale)}
              </strong>
            </div>
          )}
          <CouponBox
            code={couponText}
            onCode={setCouponText}
            applied={coupon}
            error={couponError}
            busy={couponBusy}
            savings={(() => {
              const c = chargeFor(provider ?? (rate ? "mercadopago" : "stripe"));
              return c && c.couponMinor > 0 ? formatCharge(c.couponMinor, c.currency, locale) : null;
            })()}
            onApply={async () => {
              setCouponBusy(true);
              setCouponError(null);
              try {
                setCoupon(await api.checkCoupon(couponText, state.lines.map((l) => ({ variantId: l.variantId, quantity: l.quantity })), user ? undefined : email));
              } catch (err) {
                setCoupon(null);
                setCouponError(err instanceof ApiError && err.details ? ((err.details as Record<string, string>).coupon ?? "checkout.coupon.not_found") : "checkout.errors.network");
              } finally {
                setCouponBusy(false);
              }
            }}
            onRemove={() => {
              setCoupon(null);
              setCouponText("");
            }}
          />
          {provider && chargePreview(provider) && (
            <p className="muted small">{t("checkout.chargedAs", { amount: chargePreview(provider) })}</p>
          )}
        </aside>
      </div>
    </div>
  );
}

/** "¿Tenés un cupón?": checks it with the server and shows what it takes off (one per order). */
function CouponBox(props: {
  code: string;
  onCode: (v: string) => void;
  applied: CouponQuote | null;
  error: string | null;
  busy: boolean;
  savings: string | null;
  onApply: () => void;
  onRemove: () => void;
}) {
  const { t, locale } = useI18n();
  const { applied } = props;
  return (
    <div className="coupon-box">
      {applied ? (
        <>
          <p className="notice-ok">
            {t("checkout.coupon.applied", { code: applied.code, amount: couponAmount(applied, (n) => formatArs(n, locale)) })}
            {props.savings && ` · −${props.savings}`}
          </p>
          {applied.description && <p className="muted small">{applied.description}</p>}
          {applied.excludedVariantIds.length > 0 && <p className="muted small">{t("checkout.coupon.someExcluded")}</p>}
          <button type="button" className="btn btn-link btn-sm" onClick={props.onRemove}>
            {t("checkout.coupon.remove")}
          </button>
        </>
      ) : (
        <div className="coupon-form">
          <label htmlFor="co-coupon">{t("checkout.coupon.label")}</label>
          <div className="coupon-row">
            <input
              id="co-coupon"
              value={props.code}
              onChange={(e) => props.onCode(e.target.value.toUpperCase())}
              autoCapitalize="characters"
              autoComplete="off"
              aria-invalid={Boolean(props.error)}
              aria-describedby={props.error ? "co-coupon-err" : undefined}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (props.code.trim()) props.onApply();
                }
              }}
            />
            <button type="button" className="btn btn-outline btn-sm" disabled={!props.code.trim() || props.busy} onClick={props.onApply}>
              {t("checkout.coupon.apply")}
            </button>
          </div>
          {props.error && (
            <span id="co-coupon-err" className="field-error" role="alert">
              {t(props.error as MessageKey)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
